import { useCallback, useContext, useState } from 'react';
import { Camelized } from 'humps';
import { isString } from 'lodash-es';
import { useI18n } from '@namespace/i18n';
import { UserContext } from '@namespace/user';
import {
  CamelizedWithdrawResponse,
  WithdrawParams
} from '../../api/balance/types';
import { paymentsActions, STORE_PROPS, usePaymentsStore } from '../../store';
import { ApiError } from '../../types/apiError';

type SuccessCallback = () => void;
type ErrorCallback = (errorCode: string) => void;

const { WITHDRAW } = paymentsActions;

export const useWithdraw = () => {
  const [isFetching, setIsFetching] = useState(false);
  const activeServiceId = usePaymentsStore(STORE_PROPS.ACTIVE_SERVICE_ID);
  const [user] = useContext(UserContext);
  const { id } = user;
  const { language } = useI18n();

  const [resultData, setResultData] =
    useState<CamelizedWithdrawResponse | null>(null);

  const onWithdraw = useCallback(
    async (
      {
        amount = '',
        isNodePayments = false,
        ...params
      }: Partial<WithdrawParams>,
      onSuccess: SuccessCallback = () => {},
      onError: ErrorCallback = () => {}
    ) => {
      try {
        setIsFetching(true);
        const formattedAmount = isNodePayments
          ? Number(amount)
          : String(amount).replace(',', '.');

        const response = await WITHDRAW({
          ...params,
          amount: (formattedAmount || amount) as string,
          serviceId: activeServiceId,
          userId: id,
          lang: isNodePayments ? language : undefined,
          isNodePayments
        });
        const { response: data = {} } = response;
        setResultData(
          isString(data)
            ? null
            : { ...(data as Camelized<Record<string, unknown>>), ...response }
        );
        await onSuccess();
        return response;
      } catch (response) {
        const { errorCode } = response as ApiError;
        onError(errorCode);
      } finally {
        setIsFetching(false);
      }
      return null;
    },
    [activeServiceId, id, language]
  );

  return {
    onWithdraw,
    resultData,
    setResultData,
    isFetching
  };
};
